import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import type { LoQueEspera } from '@/lib/supabase/tipos-bd'

/**
 * Todo lo que está detenido esperando una decisión.
 *
 * Una sola consulta para las seis clases de espera que tiene el recorrido. La
 * alternativa —que cada pantalla mire lo suyo— es lo que había, y obligaba a
 * entrar a todas para saber si algo estaba quieto.
 */
export function useLoQueEspera() {
  const consulta = useQuery({
    queryKey: ['lo-que-espera'],
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    queryFn: async (): Promise<LoQueEspera[]> => {
      const { data, error } = await supabase.rpc('fn_lo_que_espera')

      if (error) throw new Error(mensajeDeError(error))

      return ((data ?? []) as unknown as LoQueEspera[]).map((fila) => ({
        ...fila,
        horas_esperando: Number(fila.horas_esperando),
        valor: fila.valor === null ? null : Number(fila.valor),
      }))
    },
  })

  return { pendientes: consulta.data ?? [], cargando: consulta.isLoading }
}

/** Lo que hay que refrescar cuando algo se decide: casi todo se mueve. */
function useRefrescar() {
  const clienteConsultas = useQueryClient()

  return async () => {
    await Promise.all(
      [
        'lo-que-espera',
        'pendientes-de-decision',
        'ordenes-servicio',
        'cronograma',
        'recorridos',
        'cuello-de-botella',
        'ordenes-esperando',
        'avisos',
      ].map((clave) => clienteConsultas.invalidateQueries({ queryKey: [clave] })),
    )
  }
}

export function useDecidirCotizacion() {
  const refrescar = useRefrescar()

  return useMutation({
    mutationFn: async ({
      cotizacionId,
      aprobar,
      notas,
    }: {
      cotizacionId: string
      aprobar: boolean
      notas: string | null
    }): Promise<void> => {
      const { error } = await supabase.rpc('fn_decidir_cotizacion', {
        p_cotizacion_id: cotizacionId,
        p_aprobar: aprobar,
        p_notas: notas,
      })

      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: refrescar,
  })
}

/**
 * Dar o negar las horas desde el tablero.
 *
 * Es la misma decisión de la Compuerta 2 que ya existe en el detalle de la
 * orden; se reexporta desde aquí para que el tablero no tenga que importar
 * medio módulo de precierre por una llamada.
 */
export function useSolicitarMasTiempoDesdeElTablero() {
  const refrescar = useRefrescar()

  return useMutation({
    mutationFn: async ({
      ordenTrabajoId,
      aprobar,
      notas,
      horas = null,
    }: {
      ordenTrabajoId: string
      aprobar: boolean
      notas: string | null
      /** Las que se dan; null = las que pidió. */
      horas?: number | null
    }): Promise<void> => {
      const { error } = await supabase.rpc('fn_decidir_mas_tiempo', {
        p_orden_trabajo_id: ordenTrabajoId,
        p_aprobar: aprobar,
        p_horas_aprobadas: horas,
        p_notas: notas,
      })

      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: refrescar,
  })
}

/** Autorizar o negar que empiecen sin poder probar que están en el sitio. */
export function useAutorizarInicio() {
  const refrescar = useRefrescar()

  return useMutation({
    mutationFn: async ({
      ordenTrabajoId,
      aprobar,
      notas,
    }: {
      ordenTrabajoId: string
      aprobar: boolean
      notas: string | null
    }): Promise<void> => {
      const { error } = await supabase.rpc('fn_autorizar_inicio_sin_ubicacion', {
        p_orden_trabajo_id: ordenTrabajoId,
        p_aprobar: aprobar,
        p_notas: notas,
      })

      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: refrescar,
  })
}

export function useProgramarOrden() {
  const refrescar = useRefrescar()

  return useMutation({
    mutationFn: async ({
      ordenTrabajoId,
      colaboradorId,
      horas,
      inicio,
    }: {
      ordenTrabajoId: string
      colaboradorId: string
      horas: number
      inicio: string
    }): Promise<void> => {
      const { error } = await supabase.rpc('fn_programar_orden_trabajo', {
        p_orden_trabajo_id: ordenTrabajoId,
        p_colaborador_id: colaboradorId,
        p_horas: horas,
        p_inicio: inicio,
        // Sin fin: la base lo calcula con las horas asignadas, que es la
        // suposición razonable y queda dicha allá.
        p_fin: null,
      })

      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: refrescar,
  })
}

/**
 * Cuántas horas pide un técnico: el tablero tiene que decirlo antes de que
 * alguien pulse «Dar las horas» (Johan: «no dice cuántas está pidiendo ni
 * cuántas le quiero dar yo»). Se pide solo para las cartas de ese tipo.
 */
export function useHorasPedidas(ordenTrabajoId: string | null) {
  return useQuery({
    queryKey: ['tablero', 'horas-pedidas', ordenTrabajoId],
    enabled: Boolean(ordenTrabajoId),
    queryFn: async (): Promise<number | null> => {
      const { data, error } = await supabase
        .from('ordenes_trabajo')
        .select('horas_adicionales_solicitadas')
        .eq('id', ordenTrabajoId!)
        .maybeSingle()

      if (error) throw new Error(mensajeDeError(error))
      const h = (data as { horas_adicionales_solicitadas: number | null } | null)
        ?.horas_adicionales_solicitadas
      return h === null || h === undefined ? null : Number(h)
    },
  })
}
